import {
  CREATE_CHAT_CHANNEL,
  CREATE_CHAT_CHANNEL_SUCCESS,
  CREATE_CHAT_CHANNEL_FAILURE,
  CREATE_GROUP,
  CREATE_GROUP_FAILURE,
  CREATE_GROUP_SUCCESS,
  MESSAGE_CHAT_CHANNEL,
  MESSAGE_CHAT_CHANNEL_FAILURE,
  MESSAGE_CHAT_CHANNEL_SUCCESS,
  ADD_USERS_GROUP,
  ADD_USERS_GROUP_FAILURE,
  ADD_USERS_GROUP_SUCCESS,
  SHARE_MEDIA,
  SHARE_MEDIA_SUCCESS,
  SHARE_MEDIA_FAILURE,
  CREATE_GROUP_ADD_ID,
  CREATE_GROUP_ADD_ID_SUCCESS,
  CREATE_GROUP_ADD_ID_FAILURE,
  SELECTED_CHAT_USER,
  SEND_MESSAGE,
  MESSAGE_SETTLED,
  CLEAR_CHAT_MESSAGES,
  GLOBAL_SEARCH_CHATS_GROUPS,
  GLOBAL_SEARCH_CHATS_GROUPS_SUCCESS,
  GLOBAL_SEARCH_CHATS_GROUPS_FAILURE,
  UPLOAD_CAHT_MEDIA,
  UPLOAD_CAHT_MEDIA_SUCCESS,
  UPLOAD_CAHT_MEDIA_FAILURE,
  DELETED_MESSAGE_NOW,
  MESSAGE_DELETED_FOR_ALL,
  MESSAGE_SAVE_STATE,
  TOGGLE_SAVE_MESSAGE_FAILURE,
  SAVED_MESSAGES_LIST,
  SAVED_MESSAGES_LIST_SUCCESS,
  SAVED_MESSAGES_LIST_FAILURE,
  LOGOUT,
  LOGOUT_SUCCESS,
  LOGOUT_FAILURE,
  // Calling
  START_CALL,
  START_CALL_SUCCESS,
  START_CALL_FAILURE,
  JOIN_CALL,
  JOIN_CALL_SUCCESS,
  JOIN_CALL_FAILURE,
  RECENT_CALL_HISTORY,
  RECENT_CALL_HISTORY_SUCCESS,
  RECENT_CALL_HISTORY_FAILURE,
  MISSED_CALL_HISTORY,
  MISSED_CALL_HISTORY_SUCCESS,
  MISSED_CALL_HISTORY_FAILURE,
  USERS_DATA,
  USERS_DATA_SUCCESS,
  USERS_DATA_FAILURE,
  USER_PROFILE_DATA,
  USER_PROFILE_DATA_SUCCESS,
  USER_PROFILE_DATA_FAILURE,
  EDIT_USER_PROFILE,
  EDIT_USER_PROFILE_SUCCESS,
  EDIT_USER_PROFILE_FAILURE,
  MULTIPLE_CALL_USERS_DATA,
  MULTIPLE_CALL_USERS_DATA_SUCCESS,
  MULTIPLE_CALL_USERS_DATA_FAILURE,
  MULTIPLE_CALLER,
  EMPTY_CALL_HISTORY,
  //Threads
  THREAD_DETAILS,
  THREAD_DETAILS_SUCCESS,
  THREAD_DETAILS_FAILURE,
  THREAD_LISTING,
  THREAD_LISTING_SUCCESS,
  THREAD_LISTING_FAILURE,
  UPLOAD_THREAD_MEDIA,
  UPLOAD_THREAD_MEDIA_SUCCESS,
  UPLOAD_THREAD_MEDIA_FAILURE,
  CLEAR_CHAT_THREAD,
  THREAD_CHAT_CHANNEL,
  THREAD_CHAT_CHANNEL_SUCCESS,
  THREAD_CHAT_CHANNEL_FAILURE,
  SEND_THREAD,
  NOTIFICATIONS_LISTS,
  NOTIFICATIONS_LISTS_SUCCESS,
  NOTIFICATIONS_LISTS_FAILURE,
  NOTIFICATIONS_UNREAD_COUNT_SUCCESS,
  MARK_NOTIFICATIONS_READ_SUCCESS,
  MESSAGE_CHAT_CHANNEL_DIRECT_UPDATE,
  THREADS_CHAT_CHANNEL_DIRECT_UPDATE,
  DELETED_THREAD_MESSAGE_NOW,
  CREATE_THREADS,
  CREATE_THREADS_SUCCESS,
  CREATE_THREADS_FAILURE,
  GROUP_MEMBERS,
  GROUP_MEMBERS_SUCCESS,
  GROUP_MEMBERS_FAILURE,
  UNREAD_MESSAGE_DOT,
  UNREAD_MESSAGE_DOT_SUCCESS,
  UNREAD_MESSAGE_DOT_FAILURE,
  MISSED_CALL_COUNT,
  CHANNEL_MEMBERS_PAGE,
  CHANNEL_MEMBERS_PAGE_SUCCESS,
  CHANNEL_MEMBERS_PAGE_FAILURE,
  CONVERSATION_TOUCHED,
  CHANNEL_READ,
  CHANNEL_READ_RECEIPT,
} from '../constants';

/**
 * Append `incoming` to `held`, dropping anything already there by id.
 *
 * Every list in this reducer concatenated blind. That was survivable only while nothing
 * could serve the same row twice — and offset paging over a list that grows at the head
 * does exactly that, so a conversation could show the same message in two places with
 * two identical React keys. De-duplicating here does not make paging correct on its own
 * (a skipped row is still skipped), but it makes every remaining paging fault
 * non-destructive rather than corrupting.
 *
 * The held copy wins: it may carry local edits — an optimistic star, a "deleted" patch —
 * that a re-served row from the server would roll back.
 */
const concatById = (held, incoming, key = 'id') => {
  const base = Array.isArray(held) ? held : [];
  const rows = Array.isArray(incoming) ? incoming : [];
  if (rows.length === 0) return base;

  const seen = new Set(base.map((row) => row?.[key]).filter((id) => id !== undefined));
  const fresh = rows.filter((row) => {
    const id = row?.[key];
    if (id === undefined) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return fresh.length === 0 ? base : [...base, ...fresh];
};

/** `concatById`, prepending — for the message streams, which page backwards. */
const prependById = (held, incoming, key = 'id') => {
  const base = Array.isArray(held) ? held : [];
  const rows = Array.isArray(incoming) ? incoming : [];
  if (rows.length === 0) return base;

  const seen = new Set(base.map((row) => row?.[key]).filter((id) => id !== undefined));
  const fresh = rows.filter((row) => {
    const id = row?.[key];
    if (id === undefined) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return fresh.length === 0 ? base : [...fresh, ...base];
};

const initialState = {
  // False: nothing has been requested at boot. Declared `true`, the details
  // panel showed a spinner over an empty grid before anything was ever asked
  // for — and stayed on it for good if the fetch never fired.
  share_media_loader: false,
  // Per-domain, like share_media_loader / upload_chat_loader / call_history_loader.
  // The generic `loader` below is set by fourteen unrelated actions, so a spinner
  // keyed on it flashed over the message stream during a global search, a profile
  // load or a call start.
  message_chats_loader: false,
  threads_chats_loader: false,
  selected_chat_user: null,
  create_chat_id: null,
  messages_length: 0,
  search_by_id: [],
  chat_list: [],
  // Server cursors. `null` after a load means the list is exhausted; it is the only
  // exhaustion signal the containers act on. A `*_count` is advisory — a count and a
  // page are two reads of a table still being written to, and a client that stops on
  // `loaded >= count` stops early, late or never depending on which way they disagree.
  chat_list_next_cursor: null,
  message_next_cursor: null,
  thread_next_cursor: null,
  create_group_id: [],
  message_chats: [],
  /**
   * Which channel `message_chats` (and `threads_chats`) actually holds.
   *
   * The rows used to be anonymous, and a conversation is selected — `SELECTED_CHAT_USER`,
   * dispatched by the sidebar on click — one commit BEFORE the pane gets to clear the
   * previous stream. In that window every reader saw the new channel's id sitting beside
   * the old channel's messages and had no way to tell. Both of the panes' scroll
   * behaviours were driven off it and both broke on every conversation switch: the
   * open-at-newest pin fired against the outgoing conversation's rows and then treated
   * the incoming page as already handled, and the pager recorded the outgoing row count
   * as its baseline, so the incoming page looked like it had added nothing and history
   * was marked exhausted — which unmounts the sentinel, so scrolling up fetched nothing
   * for the rest of the visit.
   *
   * A late page for a channel already navigated away from also lands here, and marks
   * itself as what it is rather than passing as the current one.
   */
  message_chats_channel_id: null,
  threads_chats_channel_id: null,
  message_count: 0,
  /**
   * When every other live member had read everything up to, per channel — the timestamp
   * a double tick means, computed server-side from `chat_members.last_read_at`.
   *
   * Keyed by channel because the group pane has two open at once (the group and a
   * thread), and because a receipt can arrive for a conversation that is not on screen.
   * A single field was overwritten by whichever landed last.
   *
   * Null for a channel means "not read by everyone", which renders as a single tick.
   * Absent means not yet known, which renders the same way but is not a claim.
   */
  others_read_at: {},
  add_user_group: [],
  upload_chat_media: [],
  share_media: [],
  share_media_count: 0,
  share_media_length: 0,
  employee_length: 0,
  group_members: null,
  // The scrollable roster, paged from `/chat/group/:id/members`. Separate from
  // `group_members`, which is the channel record plus one bounded roster and is what the
  // header and the permission checks read.
  channel_members: [],
  channel_members_count: 0,
  channel_members_channel: null,
  channel_members_loader: false,
  loader: false,
  upload_chat_loader: false,
  edit_profile_loader: false,
  group_loader: false,
  listing_loader: false,
  //Calling
  start_call: null,
  join_call: null,
  missed_call_history: [],
  missed_call_history_count: 0,
  recent_call_history: [],
  recent_call_history_count: 0,
  call_history_loader: true,
  users_data: [],
  user_profile_data: null,
  multiple_call_users_data: [],
  multiple_caller: [],
  //Threads
  threads_listing: [],
  threads_listing_count: 0,
  threads_listing_loader: false,
  thread_details: null,
  thread_details_loader: false,
  upload_thread_loader: false,
  upload_thread_media: [],
  threads_chats: [],
  thread_length: 0,
  thread_count: 0,
  create_thread_loader: false,
  // Notifications. The feed is filtered SERVER-side by tab, so `notificaiton_count` is
  // the total for the loaded tab (what the list pages against) and `notificaiton_kind`
  // records which tab the rows belong to — without it, switching tabs paints the old
  // tab's rows under the new label until the request lands.
  notificaiton_list: [],
  notificaiton_count: 0,
  notificaiton_kind: 'all',
  notificaiton_loader: true,
  // Unread totals per tab, from `GET /user/notifications/unread-count`.
  notificaiton_unread: { all: 0, messages: 0, mentions: 0 },
  unread_message_count: 0,
  missedcallcount: 0,
  // Saved messages (bookmarks). `saved_messages_count` is the server's total for the
  // current filter, which is what the list pages against — `saved_messages.length` is
  // only how much has been loaded so far.
  saved_messages: [],
  saved_messages_count: 0,
  saved_messages_loader: false,
};

export default function MsgReducer(state = initialState, action) {
  switch (action.type) {
    case LOGOUT:
      state = {
        ...state,
        loader: true,
      };
      break;
    case LOGOUT_SUCCESS:
      state = {
        ...state,
        chat_list: [],
        loader: false,
      };
      break;
    case LOGOUT_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case CREATE_CHAT_CHANNEL:
      state = {
        ...state,
        loader: true,
      };
      break;
    case CREATE_CHAT_CHANNEL_SUCCESS:
      state = {
        ...state,
        create_chat_id: action.payload,
        message_chats: [],
        message_chats_channel_id: null,
        share_media: [],
        loader: false,
      };
      break;
    case CREATE_CHAT_CHANNEL_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case SHARE_MEDIA:
      state = {
        ...state,
        share_media_loader: true,
      };
      break;
    case SHARE_MEDIA_SUCCESS: {
      const { shareMediaLength, shareMediaData, shareMediaCount, shareMediaOffset } =
        action.payload;
      state = {
        ...state,
        // Offset 0 is a fresh read, so it REPLACES. Appending unconditionally
        // meant reopening the details panel re-requested the first page and
        // stacked a second copy of it onto the list.
        share_media: shareMediaOffset
          ? concatById(state.share_media, shareMediaData)
          : (shareMediaData ?? []),
        share_media_count: shareMediaCount,
        // Was `share_media_length`. Every consumer reads `share_media_length`, so
        // the loaded-so-far count was permanently undefined and the "have we got
        // everything?" guard in the scroll handlers never held.
        share_media_length: shareMediaOffset
          ? (state.share_media_length || 0) + shareMediaLength
          : shareMediaLength,
        share_media_loader: false,
      };
      break;
    }
    case SHARE_MEDIA_FAILURE:
      state = {
        ...state,
        share_media_loader: false,
      };
      break;

    // Saving a profile uploads the new avatar in the same request, so one flag
    // covers both. EDIT_USER_PROFILE had no case at all, so nothing on the
    // profile pane could tell that a save was in flight.
    case EDIT_USER_PROFILE:
      state = {
        ...state,
        edit_profile_loader: true,
      };
      break;
    case EDIT_USER_PROFILE_SUCCESS:
      state = {
        ...state,
        edit_profile_loader: false,
      };
      break;
    case EDIT_USER_PROFILE_FAILURE:
      state = {
        ...state,
        edit_profile_loader: false,
      };
      break;

    case UPLOAD_CAHT_MEDIA:
      state = {
        ...state,
        upload_chat_loader: true,
      };
      break;
    case UPLOAD_CAHT_MEDIA_SUCCESS:
      state = {
        ...state,
        upload_chat_media: action.payload,
        upload_chat_loader: false,
      };
      break;
    case UPLOAD_CAHT_MEDIA_FAILURE:
      state = {
        ...state,
        upload_chat_loader: false,
      };
      break;

    case MESSAGE_CHAT_CHANNEL:
      state = {
        ...state,
        loader: true,
        message_chats_loader: true,
      };
      break;
    case MESSAGE_CHAT_CHANNEL_SUCCESS: {
      const { length, reversedArr, count, nextCursor, replace, othersReadAt, channelId } =
        action.payload;

      state = {
        ...state,
        // Pages arrive oldest-first and go on the FRONT — the stream reads downwards in
        // time. `replace` is the no-cursor case: a first read, which is a new list
        // rather than more of the current one.
        message_chats: replace
          ? (reversedArr ?? [])
          : prependById(state.message_chats, reversedArr),
        // Only a replacing page can change which channel the stream belongs to — an
        // older page is, by construction, more of the channel already held.
        ...(replace ? { message_chats_channel_id: channelId ?? null } : {}),
        messages_length: length,
        message_count: count,
        // null means the server has nothing older. It is the ONLY exhaustion signal the
        // scroll handler acts on — `message_count` is advisory.
        message_next_cursor: nextCursor ?? null,
        // Seeded on a first page only; an older page carries the same channel-level value
        // and there is no reason to let a slow page-3 response overwrite a receipt that
        // has landed since.
        ...(replace && channelId
          ? { others_read_at: { ...state.others_read_at, [channelId]: othersReadAt ?? null } }
          : {}),
        loader: false,
        message_chats_loader: false,
      };

      break;
    }

    /**
     * This client read a channel. Zero that row's badge where the list already holds it,
     * rather than re-reading the list — see CHANNEL_READ in the constants for what the
     * refetch it replaces destroyed.
     */
    case CHANNEL_READ: {
      const { channel_id, others_read_at } = action.payload ?? {};
      if (!channel_id) break;

      const held = state.chat_list?.data;
      const clear = (row) =>
        String(row?.id) !== String(channel_id) || !row?.chat_member
          ? row
          : { ...row, chat_member: { ...row.chat_member, unread_message_count: 0 } };

      state = {
        ...state,
        ...(held
          ? {
              chat_list: {
                ...state.chat_list,
                data: {
                  ...held,
                  groups: { ...held.groups, rows: (held.groups?.rows ?? []).map(clear) },
                  chats: { ...held.chats, rows: (held.chats?.rows ?? []).map(clear) },
                },
              },
            }
          : {}),
        // The ack carries the reader's OWN view of read-by-everyone: their outgoing
        // messages may have become read-by-all while they were away from the tab. Absent
        // (rather than null) when the server did not say, so it is not overwritten.
        ...(others_read_at !== undefined
          ? { others_read_at: { ...state.others_read_at, [channel_id]: others_read_at } }
          : {}),
      };
      break;
    }

    /**
     * Somebody ELSE read a channel this client is in. Moves the read-by-everyone
     * timestamp; it must NOT touch the unread badge — the peer opening the conversation
     * says nothing about whether this reader has seen anything.
     */
    case CHANNEL_READ_RECEIPT: {
      const { channel_id, others_read_at = null } = action.payload ?? {};
      if (!channel_id) break;
      if (state.others_read_at?.[channel_id] === others_read_at) break;

      state = {
        ...state,
        others_read_at: { ...state.others_read_at, [channel_id]: others_read_at },
      };
      break;
    }
    case MESSAGE_CHAT_CHANNEL_DIRECT_UPDATE:
      state = {
        ...state,
        message_chats: action.payload, // prepend reversedArr to messages
      };

      break;
    case THREADS_CHAT_CHANNEL_DIRECT_UPDATE:
      state = {
        ...state,
        threads_chats: action.payload, // prepend reversedArr to messages
      };

      break;
    case MESSAGE_CHAT_CHANNEL_FAILURE:
      state = {
        ...state,
        loader: false,
        message_chats_loader: false,
      };
      break;

    case CREATE_GROUP_ADD_ID:
      state = {
        ...state,
        group_loader: true,
      };
      break;
    case CREATE_GROUP_ADD_ID_SUCCESS:
      state = {
        ...state,
        search_by_id: action.payload,
        group_loader: false,
      };
      break;
    case CREATE_GROUP_ADD_ID_FAILURE:
      state = {
        ...state,
        group_loader: false,
        search_by_id: [],
      };
      break;

    case ADD_USERS_GROUP:
      state = {
        ...state,
        loader: true,
      };
      break;
    case ADD_USERS_GROUP_SUCCESS:
      state = {
        ...state,
        add_user_group: action.payload,
        loader: false,
      };
      break;
    case ADD_USERS_GROUP_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case SELECTED_CHAT_USER:
      state = {
        ...state,
        selected_chat_user: action.payload,
      };
      break;

    case SEND_MESSAGE:
      state = {
        ...state,
        message_chats: [...state.message_chats, ...action.payload],
        upload_chat_media: [],
      };
      break;

    /**
     * Reconcile an optimistically-inserted message with what the server returned.
     *
     * The optimistic row is matched on `temp_id`, which only the sending client
     * knows, and REPLACED in place — so the bubble the sender has been looking at
     * keeps its position and its React key stays put (no exit/enter flicker), it
     * just gains the real id, timestamp and status.
     *
     * `settled: false` marks a send that failed. The row stays on screen rather
     * than vanishing: a message that disappears reads as sent, which is the one
     * impression a failure must not leave.
     */
    case MESSAGE_SETTLED: {
      const { temp_id, message, settled = true } = action.payload ?? {};
      state = {
        ...state,
        message_chats: (state.message_chats ?? []).map((row) =>
          row?.temp_id && row.temp_id === temp_id
            ? { ...row, ...(message ?? {}), pending: false, failed: !settled }
            : row
        ),
      };
      break;
    }
    case MISSED_CALL_COUNT:
      state = {
        ...state,
        missedcallcount: action.payload,
      };
      break;
    case CLEAR_CHAT_MESSAGES:
      state = {
        ...state,
        message_chats: [], // Clear the messages
        // Cleared with the rows: an empty stream belongs to no channel, and leaving the
        // previous channel's id on it would let the pane treat the empty list as its own.
        message_chats_channel_id: null,
        // Rewound with the rows. Left set, the next conversation's first page would be
        // fetched from the previous conversation's boundary.
        message_next_cursor: null,
        share_media: [],
        share_media_count: 0,
        share_media_length: 0,
        // Cleared with the rest of it. Left set, a request that was in flight
        // for the conversation being left kept the next conversation's details
        // panel on a spinner that had nothing left to resolve it.
        share_media_loader: false,
      };
      break;

    /**
     * Tombstone one message the current user deleted.
     *
     * Both of these used to copy the ARRAY and then mutate the element inside it —
     * `messages[index].deleted_by_id = deletedBy`. The message object kept its
     * identity, so a memoised row comparing props by reference never saw the change
     * and the bubble stayed on screen undeleted. It also wrote into an object a
     * previous render may still have been holding.
     *
     * Keyed on `message_id` where the caller can supply one, falling back to the
     * index for callers that have not been migrated: an index is not stable while
     * history is paging in at the head, so a prepended page could tombstone a
     * different message than the one whose menu was used. `MESSAGE_DELETED_FOR_ALL`
     * below has always been keyed correctly; this brings these two into line.
     */
    case DELETED_MESSAGE_NOW: {
      const { index, deletedBy, message_id } = action.payload ?? {};
      state = {
        ...state,
        message_chats: (state.message_chats ?? []).map((row, i) =>
          (message_id !== undefined ? row?.id === message_id : i === index)
            ? { ...row, deleted_by_id: deletedBy }
            : row
        ),
      };
      break;
    }

    case DELETED_THREAD_MESSAGE_NOW: {
      const { indexThread, deletedByThread, message_id } = action.payload ?? {};
      state = {
        ...state,
        threads_chats: (state.threads_chats ?? []).map((row, i) =>
          (message_id !== undefined ? row?.id === message_id : i === indexThread)
            ? { ...row, deleted_by_id: deletedByThread }
            : row
        ),
      };
      break;
    }

    /**
     * A channel admin withdrew a message from everyone.
     *
     * Keyed on `message_id` rather than a list index, unlike the two cases above:
     * this arrives over a socket while history may still be paging in, so the
     * position of any given bubble is not stable. Both streams are patched because
     * a retracted message can also be quoted in a thread.
     *
     * The bubble needs no new UI — both chat views already render a "message
     * deleted" placeholder whenever `deleted_by_id` is set, and already distinguish
     * whether it was you or somebody else. Idempotent, so the retracting admin's
     * own optimistic dispatch and the socket broadcast can both land.
     */
    case MESSAGE_DELETED_FOR_ALL: {
      const { message_id, deleted_by_id } = action.payload ?? {};
      if (!message_id) break;

      const tombstone = (msg) =>
        msg?.id === message_id
          ? { ...msg, deleted_by_id: deleted_by_id ?? msg.deleted_by_id, message: null, media: [] }
          : msg;

      state = {
        ...state,
        message_chats: (state.message_chats ?? []).map(tombstone),
        threads_chats: (state.threads_chats ?? []).map(tombstone),
        // A message withdrawn from everyone is cascade-deleted from every user's
        // bookmarks server-side, so drop it here too rather than leaving a saved row
        // pointing at a tombstone until the list is next fetched.
        saved_messages: (state.saved_messages ?? []).filter((msg) => msg?.id !== message_id),
        saved_messages_count: Math.max(
          0,
          (state.saved_messages_count ?? 0) -
            ((state.saved_messages ?? []).some((msg) => msg?.id === message_id) ? 1 : 0)
        ),
      };
      break;
    }

    /**
     * The star's local state, applied optimistically and reverted on failure.
     *
     * Keyed on `message_id` and applied to BOTH streams plus the saved list itself,
     * because the same message can be on screen in more than one of them at once —
     * unstarring from the saved view has to un-star the bubble behind it as well.
     * Idempotent, so the optimistic patch and the success action can both land.
     */
    case MESSAGE_SAVE_STATE:
    case TOGGLE_SAVE_MESSAGE_FAILURE: {
      const { message_id, is_saved } = action.payload ?? {};
      if (!message_id) break;

      const patch = (msg) => (msg?.id === message_id ? { ...msg, is_saved } : msg);
      const wasListed = (state.saved_messages ?? []).some((msg) => msg?.id === message_id);

      state = {
        ...state,
        message_chats: (state.message_chats ?? []).map(patch),
        threads_chats: (state.threads_chats ?? []).map(patch),
        // Un-starring inside the saved view removes the row; re-starring is NOT added
        // back, because the row needs the enrichment only the server can supply. The
        // next fetch brings it in.
        saved_messages: is_saved
          ? (state.saved_messages ?? []).map(patch)
          : (state.saved_messages ?? []).filter((msg) => msg?.id !== message_id),
        saved_messages_count:
          !is_saved && wasListed
            ? Math.max(0, (state.saved_messages_count ?? 0) - 1)
            : (state.saved_messages_count ?? 0),
      };
      break;
    }

    case SAVED_MESSAGES_LIST:
      state = { ...state, saved_messages_loader: true };
      break;
    case SAVED_MESSAGES_LIST_SUCCESS: {
      const { data, offset } = action.payload ?? {};
      const rows = data?.rows ?? [];
      state = {
        ...state,
        // offset 0 replaces (initial load, or a new search); anything higher appends,
        // which is what makes one action serve both the first page and infinite scroll.
        saved_messages: offset ? concatById(state.saved_messages, rows) : rows,
        saved_messages_count: data?.count ?? 0,
        saved_messages_loader: false,
      };
      break;
    }
    case SAVED_MESSAGES_LIST_FAILURE:
      state = { ...state, saved_messages_loader: false };
      break;

    case CREATE_GROUP:
      state = {
        ...state,
        loader: true,
      };
      break;
    case CREATE_GROUP_SUCCESS:
      state = {
        ...state,
        create_group_id: action.payload,
        // Creating a group navigates into it, and the conversation pane clears and
        // reloads the stream itself — so clearing it here is redundant. Clearing the
        // call histories and the notification feed was never anything but a bug: their
        // containers' paging cursors do not reset with them, so the lists came back
        // empty with their cursors stranded mid-list.
        message_chats: [],
        message_chats_channel_id: null,
        message_next_cursor: null,
        loader: false,
      };
      break;
    case CREATE_GROUP_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case GLOBAL_SEARCH_CHATS_GROUPS:
      state = {
        ...state,
        loader: true,
      };
      break;
    case GLOBAL_SEARCH_CHATS_GROUPS_SUCCESS: {
      const { nextCursor, replace, ...response } = action.payload ?? {};
      const incomingGroups = response?.data?.groups?.rows ?? [];
      const incomingChats = response?.data?.chats?.rows ?? [];
      const held = state.chat_list?.data;

      state = {
        ...state,
        // The list is now PAGED, so a response is either a fresh list or more of the
        // one already held. It used to replace wholesale because it was always the
        // whole list.
        chat_list: {
          ...response,
          data: {
            ...(response?.data ?? {}),
            groups: {
              count: response?.data?.groups?.count ?? 0,
              rows: replace ? incomingGroups : concatById(held?.groups?.rows, incomingGroups),
            },
            chats: {
              count: response?.data?.chats?.count ?? 0,
              rows: replace ? incomingChats : concatById(held?.chats?.rows, incomingChats),
            },
          },
        },
        // One cursor for both halves — the server pages the merged, recency-ordered
        // channel set, which is the order the sidebar renders in.
        chat_list_next_cursor: nextCursor ?? null,
        // The three lines that used to blank `missed_call_history`, `recent_call_history`
        // and `notificaiton_list` are gone. A success handler has no business clearing
        // collections it does not own: the owning container's paging cursor is React
        // state and did NOT reset with them, so the list went to zero rows while its
        // cursor stayed at page four — and everything before that page was unreachable
        // without a remount. Each list is now reset by the container that owns it.
        loader: false,
      };
      break;
    }
    case GLOBAL_SEARCH_CHATS_GROUPS_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case GROUP_MEMBERS:
      state = {
        ...state,
        loader: true,
      };
      break;
    case GROUP_MEMBERS_SUCCESS:
      state = {
        ...state,
        group_members: action.payload,
        loader: false,
      };
      break;
    case GROUP_MEMBERS_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    //Calling
    case START_CALL:
      state = {
        ...state,
        loader: true,
      };
      break;
    case START_CALL_SUCCESS:
      state = {
        ...state,
        start_call: action.payload,
        loader: false,
      };
      break;
    case START_CALL_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case JOIN_CALL:
      state = {
        ...state,
        loader: true,
      };
      break;
    case JOIN_CALL_SUCCESS:
      state = {
        ...state,
        join_call: action.payload,
        loader: false,
      };
      break;
    case JOIN_CALL_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case MISSED_CALL_HISTORY:
      state = {
        ...state,
        call_history_loader: true,
      };
      break;
    case MISSED_CALL_HISTORY_SUCCESS: {
      const { countCallsMissed, CallsDataMissed, replace } = action.payload;
      state = {
        ...state,
        // No longer blanks `recent_call_history` and `notificaiton_list`. Recent and
        // Missed have independent saga watchers, so switching tabs does not cancel the
        // request already in flight — and when it landed it wiped the list the reader
        // had just switched TO, leaving an empty tab whose next page started at row 20.
        missed_call_history: replace
          ? (CallsDataMissed ?? [])
          : concatById(state.missed_call_history, CallsDataMissed),
        missed_call_history_count: countCallsMissed,
        call_history_loader: false,
      };
      break;
    }
    case MISSED_CALL_HISTORY_FAILURE:
      state = {
        ...state,
        call_history_loader: false,
      };
      break;

    case RECENT_CALL_HISTORY:
      state = {
        ...state,
        call_history_loader: true,
      };
      break;
    case RECENT_CALL_HISTORY_SUCCESS: {
      const { countCalls, CallsData, replace } = action.payload;
      state = {
        ...state,
        recent_call_history: replace
          ? (CallsData ?? [])
          : concatById(state.recent_call_history, CallsData),
        recent_call_history_count: countCalls,
        call_history_loader: false,
      };
      break;
    }
    case RECENT_CALL_HISTORY_FAILURE:
      state = {
        ...state,
        call_history_loader: false,
      };
      break;
    // Scoped to what its name says. It also blanked `threads_listing` and
    // `notificaiton_list`, and the message pane dispatches it on mount — so opening any
    // conversation silently emptied the notification feed behind it, leaving that
    // container's paging cursor pointing into a list that no longer existed.
    case EMPTY_CALL_HISTORY:
      state = {
        ...state,
        missed_call_history: [],
        missed_call_history_count: 0,
        recent_call_history: [],
        recent_call_history_count: 0,
      };
      break;

    // ── Paged channel roster ───────────────────────────────────────────────────
    case CHANNEL_MEMBERS_PAGE:
      state = { ...state, channel_members_loader: true };
      break;
    case CHANNEL_MEMBERS_PAGE_SUCCESS: {
      const { channel_id, rows, count, replace } = action.payload ?? {};
      // A late page for a channel the reader has already navigated away from is
      // dropped rather than appended: the request was in flight when the pane changed,
      // and its rows belong to a roster nobody is looking at.
      const stale = !replace && state.channel_members_channel !== channel_id;
      state = stale
        ? { ...state, channel_members_loader: false }
        : {
            ...state,
            channel_members: replace ? (rows ?? []) : concatById(state.channel_members, rows),
            channel_members_count: count ?? 0,
            channel_members_channel: channel_id,
            channel_members_loader: false,
          };
      break;
    }
    case CHANNEL_MEMBERS_PAGE_FAILURE:
      state = { ...state, channel_members_loader: false };
      break;

    /**
     * A conversation was touched by an inbound socket event.
     *
     * This replaces a full re-read of the conversation list. The sidebar used to
     * dispatch GLOBAL_SEARCH_CHATS_GROUPS on every `message:alert`, so one message
     * arriving anywhere re-fetched the reader's entire channel graph — and now that the
     * list is paged, a refetch would also throw away every page past the first and yank
     * a reader scrolled deep back to the top.
     *
     * Only patches a row already held. A message for a conversation not in the loaded
     * list — a brand-new one, or one below the last loaded page — is left to the
     * container, which reloads the first page for that case and only that case.
     */
    case CONVERSATION_TOUCHED: {
      const { channel_id, message, is_open } = action.payload ?? {};
      const held = state.chat_list?.data;
      if (!channel_id || !held) break;

      const touch = (row) => {
        if (String(row?.id) !== String(channel_id)) return row;
        const unread = Number(row?.chat_member?.unread_message_count) || 0;
        return {
          ...row,
          // Moves the row to the top of the recency sort the sidebar applies.
          updatedAt: new Date().toISOString(),
          chats: message ? [{ ...(row?.chats?.[0] ?? {}), message }] : row?.chats,
          chat_member: row?.chat_member
            ? {
                ...row.chat_member,
                // The badge does not move while the reader is looking at the
                // conversation — the pane marks it read on arrival.
                unread_message_count: is_open ? 0 : unread + 1,
              }
            : row?.chat_member,
        };
      };

      state = {
        ...state,
        chat_list: {
          ...state.chat_list,
          data: {
            ...held,
            groups: { ...held.groups, rows: (held.groups?.rows ?? []).map(touch) },
            chats: { ...held.chats, rows: (held.chats?.rows ?? []).map(touch) },
          },
        },
      };
      break;
    }

    case USERS_DATA:
      state = {
        ...state,
        loader: true,
      };
      break;
    case USERS_DATA_SUCCESS:
      state = {
        ...state,
        users_data: action?.payload,
      };
      break;
    case USERS_DATA_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case USER_PROFILE_DATA:
      state = {
        ...state,
        loader: true,
      };
      break;
    case USER_PROFILE_DATA_SUCCESS:
      state = {
        ...state,
        user_profile_data: action?.payload,
      };
      break;
    case USER_PROFILE_DATA_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case MULTIPLE_CALL_USERS_DATA:
      state = {
        ...state,
        loader: true,
      };
      break;
    case MULTIPLE_CALL_USERS_DATA_SUCCESS:
      state = {
        ...state,
        multiple_call_users_data: action?.payload,
      };
      break;
    case MULTIPLE_CALL_USERS_DATA_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;
    case MULTIPLE_CALLER:
      state = {
        ...state,
        multiple_caller: action?.payload,
      };
      break;

    case CLEAR_CHAT_THREAD:
      state = {
        ...state,
        threads_details: [],
        threads_chats: [],
        threads_chats_channel_id: null,
        thread_next_cursor: null,
      };
      break;
    case THREAD_LISTING:
      state = {
        ...state,
        threads_listing_loader: true,
      };
      break;
    case THREAD_LISTING_SUCCESS: {
      const { ThreadListingData, ThreadListingCount, replace } = action.payload;
      state = {
        ...state,
        threads_listing: replace
          ? (ThreadListingData ?? [])
          : concatById(state.threads_listing, ThreadListingData),
        threads_listing_count: ThreadListingCount,
        threads_listing_loader: false,
      };
      break;
    }
    case THREAD_LISTING_FAILURE:
      state = {
        ...state,
        threads_listing_loader: false,
      };
      break;

    case THREAD_DETAILS:
      state = {
        ...state,
        thread_details_loader: true,
      };
      break;
    case THREAD_DETAILS_SUCCESS:
      state = {
        ...state,
        thread_details: action.payload,
        thread_details_loader: false,
      };
      break;
    case THREAD_DETAILS_FAILURE:
      state = {
        ...state,
        thread_details_loader: false,
      };
      break;

    case UPLOAD_THREAD_MEDIA:
      state = {
        ...state,
        upload_thread_loader: true,
      };
      break;
    case UPLOAD_THREAD_MEDIA_SUCCESS:
      state = {
        ...state,
        upload_thread_media: action.payload,
        upload_thread_loader: false,
      };
      break;
    case UPLOAD_THREAD_MEDIA_FAILURE:
      state = {
        ...state,
        upload_thread_loader: false,
      };
      break;
    case SEND_THREAD:
      state = {
        ...state,
        threads_chats: [...state.threads_chats, ...action.payload],
        upload_thread_media: [],
      };
      break;
    case THREAD_CHAT_CHANNEL:
      state = {
        ...state,
        loader: true,
        threads_chats_loader: true,
      };
      break;
    case THREAD_CHAT_CHANNEL_SUCCESS: {
      const { lengthThread, threadArr, countThread, nextCursor, replace, othersReadAt, channelId } =
        action.payload;
      state = {
        ...state,
        threads_chats: replace ? (threadArr ?? []) : prependById(state.threads_chats, threadArr),
        ...(replace ? { threads_chats_channel_id: channelId ?? null } : {}),
        thread_length: lengthThread,
        thread_count: countThread,
        thread_next_cursor: nextCursor ?? null,
        // Keyed by the thread's own channel id, alongside the parent group's — both are on
        // screen at once, and a single field was overwritten by whichever landed last.
        ...(replace && channelId
          ? { others_read_at: { ...state.others_read_at, [channelId]: othersReadAt ?? null } }
          : {}),
        loader: false,
        threads_chats_loader: false,
      };

      break;
    }
    case THREAD_CHAT_CHANNEL_FAILURE:
      state = {
        ...state,
        loader: false,
        threads_chats_loader: false,
      };
      break;
    case NOTIFICATIONS_LISTS:
      state = {
        ...state,
        notificaiton_loader: true,
      };
      break;
    case NOTIFICATIONS_LISTS_SUCCESS: {
      const { notificationListRows, notificationListCount, notificationListKind, replace } =
        action.payload;
      const rows = notificationListRows || [];
      state = {
        ...state,
        notificaiton_list: replace ? rows : concatById(state.notificaiton_list, rows),
        notificaiton_count: notificationListCount,
        notificaiton_kind: notificationListKind,
        notificaiton_loader: false,
      };

      break;
    }
    case NOTIFICATIONS_UNREAD_COUNT_SUCCESS:
      state = {
        ...state,
        notificaiton_unread: { ...state.notificaiton_unread, ...action.payload },
      };
      break;
    // Reflect the read locally rather than refetching the feed: the server has
    // already committed it, and a refetch would reset the reader's scroll.
    case MARK_NOTIFICATIONS_READ_SUCCESS: {
      const ids = action.payload?.ids;
      const target = ids ? new Set(ids) : null;
      state = {
        ...state,
        notificaiton_list: state.notificaiton_list.map((item) =>
          !target || target.has(item?.id) ? { ...item, status: 1 } : item
        ),
      };
      break;
    }
    case NOTIFICATIONS_LISTS_FAILURE:
      state = {
        ...state,
        notificaiton_loader: false,
      };
      break;

    case CREATE_THREADS:
      state = {
        ...state,
        create_thread_loader: true,
      };
      break;
    case CREATE_THREADS_SUCCESS:
      state = {
        ...state,
        create_thread_loader: false,
      };

      break;
    case CREATE_THREADS_FAILURE:
      state = {
        ...state,
        create_thread_loader: false,
      };
      break;
    case UNREAD_MESSAGE_DOT:
      state = {
        ...state,
      };

      break;
    case UNREAD_MESSAGE_DOT_SUCCESS:
      state = {
        ...state,
        unread_message_count: action?.payload?.data?.isUnreadCount,
      };

      break;
    case UNREAD_MESSAGE_DOT_FAILURE:
      state = {
        ...state,
      };
      break;

    default:
      break;
  }
  return state;
}
